import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { xcg, humanWaiting, humanArrival, cwTodayISO } from '../lib/format';
import { isArrivingToday, isInHouse, isDepartingToday, isProofWaiting } from '../lib/filters';
import { StatusBadge } from '../components/StatusBadge';
import { CalendarCheck, BedDouble, DoorOpen, Wallet, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useRegisterStatus } from '../context/RegisterStatus';

export default function Today() {
  const [bookings, setBookings] = useState(null);
  const [err, setErr] = useState('');
  const [checkingIn, setCheckingIn] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();
  const { isStale } = useRegisterStatus() || {};

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/bookings');
      setBookings(data);
    } catch {
      setErr('Could not reach the booking register. Retrying.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err && !bookings) return <div className="text-[#B26A00]">{err}</div>;
  if (!bookings) return <div className="flex items-center gap-2 text-[#333]/70"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  const arriving = bookings.filter(isArrivingToday);
  const inHouse = bookings.filter(isInHouse);
  const departing = bookings.filter(isDepartingToday);
  const waiting = bookings.filter(isProofWaiting).sort((a,b)=> a.CreatedAt.localeCompare(b.CreatedAt));

  const doCheckIn = async (bn) => {
    setCheckingIn(bn);
    try {
      await api.post('/bookings/check-in', { booking_number: bn });
      toast.success('Guest checked in');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not check in');
    } finally {
      setCheckingIn(null);
    }
  };

  const canCheckIn = (user?.role === 'front_desk' || user?.role === 'manager') && !isStale;

  const counters = [
    { key: 'arriving', label: 'Arriving today', value: arriving.length, icon: CalendarCheck, tint: '#E8F5E9', fg: '#1F5A24', to: '/arrivals' },
    { key: 'inhouse', label: 'In house', value: inHouse.length, icon: BedDouble, tint: '#FBFCFB', fg: '#333', to: '/register' },
    { key: 'departing', label: 'Departing today', value: departing.length, icon: DoorOpen, tint: '#FBFCFB', fg: '#333', to: '/register' },
    { key: 'waiting', label: 'Proofs waiting', value: waiting.length, icon: Wallet, tint: '#FFF3E0', fg: '#B26A00', to: '/payments' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Good day, {user?.name?.split(' ')[0] || 'team'}.</h1>
        <p className="text-[#333]/70 mt-1">Here is where things stand today.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        {counters.map((c) => (
          <button
            key={c.key}
            data-testid={`kpi-${c.key}`}
            onClick={() => nav(c.to)}
            className="text-left bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-4 sm:p-5 hover:border-[#2E7D32] hover:shadow-sm transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: c.tint, color: c.fg }}>
                <c.icon size={18} />
              </div>
              <ChevronRight size={16} className="ml-auto text-[#333]/40" />
            </div>
            <div className="mt-3 font-heading text-4xl leading-none text-[#1F5A24]">{c.value}</div>
            <div className="mt-1 text-sm text-[#333]/70">{c.label}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl">
          <header className="px-5 py-4 border-b border-[#CFD8CF] flex items-center justify-between">
            <h2 className="font-heading text-xl text-[#1F5A24]">Today's arrivals</h2>
            <button onClick={() => nav('/arrivals')} className="text-sm text-[#2E7D32] hover:underline">See all</button>
          </header>
          <div className="p-3 sm:p-4">
            {arriving.length === 0 ? (
              <EmptyMsg text="No arrivals today. Enjoy the calm." />
            ) : (
              <ul className="divide-y divide-[#CFD8CF]">
                {arriving.map((b) => (
                  <li key={b['Booking#']} className="py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{b.Name}</div>
                      <div className="text-sm text-[#333]/70 truncate">{b.RoomType} · {b.Guests} guest{b.Guests>1?'s':''}</div>
                    </div>
                    {canCheckIn && (
                      <button
                        data-testid={`checkin-${b['Booking#']}`}
                        disabled={checkingIn === b['Booking#']}
                        onClick={() => doCheckIn(b['Booking#'])}
                        className="px-4 py-2 rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white text-sm font-medium min-h-[2.75rem] disabled:opacity-60"
                      >
                        {checkingIn === b['Booking#'] ? '…' : 'Check in'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl">
          <header className="px-5 py-4 border-b border-[#CFD8CF] flex items-center justify-between">
            <h2 className="font-heading text-xl text-[#1F5A24]">Payment proofs waiting</h2>
            {(user?.role === 'finance' || user?.role === 'manager') && (
              <button onClick={() => nav('/payments')} className="text-sm text-[#2E7D32] hover:underline">Review</button>
            )}
          </header>
          <div className="p-3 sm:p-4">
            {waiting.length === 0 ? (
              <EmptyMsg text="Nothing waiting. Everyone's paid up." />
            ) : (
              <ul className="divide-y divide-[#CFD8CF]">
                {waiting.slice(0, 8).map((b) => (
                  <li key={b['Booking#']} className="py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{b.Name}</div>
                      <div className="text-sm text-[#333]/70 truncate">{xcg(b.Amount)} · {humanWaiting(b.CreatedAt)}</div>
                    </div>
                    <StatusBadge status={b.PaymentStatus} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function EmptyMsg({ text }) {
  return <div className="text-[#333]/60 py-6 px-2">{text}</div>;
}
